import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import type { NewTextBoxStyle } from '../types/new-text-box-style'

/**
 * The new-text-box style after a TypePanel update. Only kerning, tracking, underline and alignment
 * carry over to the next new frame.
 *
 * Font family, font and point size are ignored on purpose: changing one frame's font (Abyss →
 * Agnes for one word) must never change the font the next new frame starts in — that is always
 * the book/checkpoint default. This has regressed repeatedly; don't add them back.
 *
 * Returns `prev` itself when nothing it tracks changed, so a font-only update doesn't re-render.
 */
export function nextNewTextBoxStyle(
  prev: NewTextBoxStyle,
  update: TextRunStyleUpdate
): NewTextBoxStyle {
  const next: NewTextBoxStyle = {
    ...prev,
    ...(update.kerning !== undefined ? { kerning: update.kerning } : {}),
    ...(update.tracking !== undefined ? { tracking: update.tracking } : {}),
    ...(update.underline !== undefined ? { underline: update.underline } : {}),
    ...(update.alignment !== undefined ? { alignment: update.alignment } : {}),
  }

  const changed =
    next.kerning !== prev.kerning ||
    next.tracking !== prev.tracking ||
    next.underline !== prev.underline ||
    next.alignment !== prev.alignment

  return changed ? next : prev
}
